import {
  Header,
  QuestionnaireFormProps,
  QuestionnaireQuestions,
  ScreenProps as QuestionnaireScreenProps,
} from '@getpopsure/qnr-framework';
import { MultiDropzone } from '@popsure/dirty-swan';
import classNames from 'classnames';
import Markdown from 'components/markdown';
import { useUploadFiles } from 'hooks/useUploadFiles';
import { useEffect } from 'react';
import { UploadedFile } from 'shared/models/types';

import { LegalAndContinueButton } from './LegalAndContinueButton';
import styles from './style.module.scss';

// While it's called "continueButtonText" in Strapi UI, the ScreenProps type exposes "buttonText" instead of continueButtonText
type ScreenProps = Omit<QuestionnaireScreenProps, 'buttonText'> & {
  continueButtonText?: string;
};

type DisclaimerParagraphData = {
  text: string;
  icon?: {
    src: string;
    alt: string;
    width?: string;
    height?: string;
  };
};

const DisclaimerParagraph = ({ text, icon }: DisclaimerParagraphData) => (
  <div className={classNames('mb16 d-flex gap8', styles.disclaimerParagraph)}>
    {icon && (
      <img
        className={styles.mt2}
        src={icon.src}
        alt=""
        width={icon.width}
        height={icon.height}
      />
    )}
    <Markdown paragraphClassName="p-p">{text}</Markdown>
  </div>
);

export const MultiUpload = <Questionnaire extends QuestionnaireQuestions>({
  value,
  onSubmitValue,
  setValidForSubmission,
  showLegalNotice,
  maxFiles,
  isQuestionnaire,
  title,
  description,
  continueButtonText,
  disclaimerParagraphs = [],
  supportsText,
  skipDetails,
}: QuestionnaireFormProps<Questionnaire> &
  ScreenProps & {
    title: string;
    description?: string;
    showLegalNotice?: boolean;
    maxFiles?: number;
    isQuestionnaire?: boolean;
    disclaimerParagraphs?: DisclaimerParagraphData[];
    supportsText?: string;
  }) => {
  const {
    uploadFile,
    uploadedFiles,
    loading: uploadLoading,
    removeFile,
    isSubmitValid,
  } = useUploadFiles({
    initialValue: (value as UploadedFile[]) ?? [],
    isQuestionnaire,
  });

  useEffect(() => {
    setValidForSubmission(uploadedFiles.length > 0 && !uploadLoading);
  }, [uploadedFiles, setValidForSubmission, uploadLoading]);

  const handleSubmit = async () => {
    if (uploadedFiles.length < 1 || !isSubmitValid) {
      throw new Error(`[SignupQuestionnaire] Document token(s) is missing`);
    }

    onSubmitValue(uploadedFiles);
  };

  return (
    <Header onSubmit={handleSubmit}>
      <div
        className={classNames(
          'mt80 p-body d-flex ai-center jc-center',
          styles.container
        )}
      >
        <div className={classNames('d-flex f-wrap c-gap48 w100', styles.grid)}>
          <div className={styles.half}>
            <h1 className="p-h1 mb8">{title}</h1>

            {description && (
              <div className="mb24">
                <Markdown
                  paragraphClassName={classNames('p-p', styles.paragraph)}
                >
                  {description}
                </Markdown>
              </div>
            )}
            {disclaimerParagraphs.map((paragraphData) => (
              <DisclaimerParagraph
                {...paragraphData}
                key={paragraphData.text}
              />
            ))}
            <div className={classNames(styles.hideOnMobile, styles.mt60)}>
              <LegalAndContinueButton
                isSubmitValid={isSubmitValid}
                showLegalNotice={showLegalNotice}
                continueButtonText={continueButtonText}
                skipDetails={skipDetails}
                onSubmitValue={onSubmitValue}
              />
            </div>
          </div>

          <div className={styles.half}>
            <MultiDropzone
              onFileSelect={uploadFile}
              uploadedFiles={uploadedFiles}
              uploading={uploadLoading}
              onRemoveFile={removeFile}
              maxFiles={maxFiles}
              textOverrides={{
                supportsText,
              }}
            />
          </div>

          <div className={classNames('w100', styles.hideOnDesktop)}>
            <LegalAndContinueButton
              isSubmitValid={isSubmitValid}
              showLegalNotice={showLegalNotice}
              continueButtonText={continueButtonText}
              skipDetails={skipDetails}
              onSubmitValue={onSubmitValue}
            />
          </div>
        </div>
      </div>
    </Header>
  );
};
